<script lang="ts">
  import { logComponentEvent } from '@/lib/native-messaging/log-event'
  import { flyAndScale } from '@/lib/utils/transitions'
  import { shouldLockServerSelect, isInternalMode } from '@/params'
  import { isGlobalLoaderVisible } from '@/stores'
  import { Select, type Selected } from 'bits-ui'
  import { _ } from 'svelte-i18n'
  import NewGlobe from './icons/new-globe.svelte'
  import { cn } from '@/lib/utils/cn'

  export let readOnly = false

  const isChina = window.location.host.includes('upwardsware')
  const isDevelop = window.location.host.includes('develop-cluster')
  const isStaging = window.location.host.includes('staging')

  // Allow showing staging/develop options when in debug mode or already on those servers
  const canShowStagingAndDevelop = isInternalMode || isDevelop || isStaging

  // Only disable if locked by param, readOnly
  const disabled = shouldLockServerSelect || readOnly

  type Server = 'global' | 'china' | 'staging' | 'develop'

  const urlByServer = {
    global: 'https://auth.seekrtech.com/entry',
    china: 'https://auth.upwardsware.com/entry',
    staging: 'https://auth.staging.seekrtech.com/entry',
    develop: 'https://auth.service.develop-cluster.seekrtech.com/entry',
  } satisfies Record<Server, string>

  const optionByServer = {
    global: { label: $_('global_server'), value: 'global' },
    china: { label: $_('china_server'), value: 'china' },
    ...(canShowStagingAndDevelop && {
      staging: { label: 'Staging server', value: 'staging' },
      develop: { label: 'Develop server', value: 'develop' },
    }),
  } as Record<Server, Selected<Server>>

  const items = Object.values(optionByServer)

  // Safe access: fallback to global if develop/staging options don't exist
  let selected =
    isDevelop && optionByServer.develop
      ? optionByServer.develop
      : isStaging && optionByServer.staging
        ? optionByServer.staging
        : isChina
          ? optionByServer.china
          : optionByServer.global

  function handleOpenChange(open: boolean) {
    if (open) {
      logComponentEvent({
        appAction: 'click',
        componentName: 'seekrid_choose_server',
        type: 'btn',
      })
    }
  }

  function handleSelectedChange(selected?: Selected<Server>) {
    if (!selected) return
    $isGlobalLoaderVisible = true
    logComponentEvent({
      appAction: 'select',
      type: 'selector',
      componentName: 'server_select',
      componentValue: `${selected.value}_server`,
    })
    window.postMessage({
      type: 'change_server_region',
      payload: {
        region: selected.value,
      },
    })
    window.location.href = urlByServer[selected.value] + location.search
  }
</script>

<Select.Root {items} bind:selected onSelectedChange={handleSelectedChange} onOpenChange={handleOpenChange}>
  <Select.Trigger
    class="fixed right-4 top-[68px] focus-within:outline-none"
    {disabled}
  >
    <NewGlobe />
  </Select.Trigger>

  <Select.Content
    transition={flyAndScale}
    class="flex flex-col gap-3 rounded-[26px] bg-white/65 p-5 shadow-[0_0_20px_0_#00000040] ring-1 ring-inset ring-white backdrop-blur"
    sameWidth={false}
    align="start"
    sideOffset={-36}
  >
    {#each items as item, index}
      <Select.Item value={item.value} label={item.label} let:isSelected>
        <span
          class={cn(
            'flex h-[35px] min-w-[168px] items-center gap-3 rounded-[22px] px-[10px] text-sm',
            isSelected && 'bg-black/5 font-semibold',
          )}
        >
          {item.label}
          <Select.ItemIndicator>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.99414 10.0001L8.32751 13.3335L15.006 6.66675"
                stroke="#333333"
                stroke-width="1.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Select.ItemIndicator>
        </span>
      </Select.Item>
    {/each}
    <Select.Arrow />
  </Select.Content>
</Select.Root>
